import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles, Button } from "@material-ui/core";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import MainContainer from "../../../components/MainContainer";
import { headerDataImmo } from "../../../data/modules/logistic-header";
import Title from "../../../components/Title";
import Spacer from "../../../components/Spacer";
import Message from "../../tools/Message";
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import DirectionService from "../../../services/DirectionService";
import MissionRentService from "../../../services/MissionRentService";
import { validateRentForm } from "../../../helpers/ValidateForm";
import { DataGrid } from "@mui/x-data-grid";


const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    marginTop: "16px",
    borderRadius: "8px",
    boxShadow:
      "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
}));


const RENT_TYPES = [
  { label: "Location voiture", value: "Location voiture" },
  { label: "Location moto", value: "Location moto" },
  { label: "Location velo", value: "Location velo" },
  { label: "A determiner", value: "other" },
];


const PAYMENT_TYPES = [
  { label: "Forfaitaire", value: "flat" },
  { label: "Paiement par jour", value: "daily" },
];


const initialFormValues = {
  rent_id: null,
  direction_id: null,
  rent_type: "",
  rent_designation: "",
  rent_paiement: "",
  rent_amount: "",
  rent_upfuel: "",
  rent_conso_100km: "",
};


const RentParameter = () => {
  const classes = useStyles();
  const directionUser = localStorage.getItem("USER_DIR") || null;
  const [directionOptions, setDirectionOptions] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState(directionUser || null);
  const [rows, setRows] = useState([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formValues, setFormValues] = useState(initialFormValues);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    rent_id: null,
  });
  const [message, setMessage] = useState({
    open: false,
    color: "green",
    messageInfo: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] =
    useState(false);


  const directionMap = useMemo(() => {
    const map = new Map();
    directionOptions.forEach((dir) => map.set(dir.value, dir.label));
    return map;
  }, [directionOptions]);


  const getDirectionOption = (directionId) =>
    directionOptions.find((dir) => dir.value === directionId) || null;


  const getRentTypeLabel = (row) => {
    if (row.rent_type === "other") {
      return row.rent_designation || "-";
    }
    return RENT_TYPES.find((type) => type.value === row.rent_type)?.label || "-";
  };


  const getPaymentLabel = (value) =>
    PAYMENT_TYPES.find((payment) => payment.value === value)?.label || "-";


  const columns = [
    {
      field: "direction_code",
      headerName: "Direction",
      flex: 1,
      width: 200,
    },
    {
      field: "rent_type",
      headerName: "Type de location",
      flex: 1,
      width: 200,
      renderCell: (params) => (
        <>{getRentTypeLabel(params.row)}</>
      ),
    },
    {
      field: "rent_paiement",
      headerName: "Mode de paiement",
      width: 180,
      flex: 1,
      renderCell: (params) => (
        <>
          {getPaymentLabel(params.row.rent_paiement)}
        </>
      ),
    },
    {
      field: "rent_amount",
      headerName: "Montant location (Ar)",
      width: 180,
    },
    {
      field: "rent_upfuel",
      headerName: "Prix unitaire carburant (Ar/L)",
      width: 220,
    },
    {
      field: "rent_conso_100km",
      headerName: "Conso /100km",
      width: 180,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => openEditDialog(params.row)}
            title="Modifier la location"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() =>
              setDeleteDialog({ open: true, rent_id: params.row.rent_id })
            }
            title="Supprimer la location"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];


  const handleCloseMessage = () =>
    setMessage((prev) => ({ ...prev, open: false }));


  const normalizeRentType = (rent) => {
    const found = RENT_TYPES.find((type) => type.value === rent.rent_type);
    if (found) {
      return {
        rent_type: rent.rent_type,
        rent_designation: "",
      };
    }
    return {
      rent_type: "other",
      rent_designation: rent.rent_type,
    };
  };


  const mapRentToRow = (rent, index) => {
    const normalizedType = normalizeRentType(rent);
    const rentId = rent.mission_rent_id || rent.rent_id || rent.id || index;
    return {
      id: rentId,
      rent_id: rentId,
      direction_id: rent.direction_id,
      direction_code : rent.direction_code || '',
      rent_type: normalizedType.rent_type,
      rent_designation: normalizedType.rent_designation,
      rent_paiement: rent.rent_paiement,
      rent_amount: rent.rent_amount,
      rent_upfuel: rent.rent_upfuel,
      rent_conso_100km: rent.rent_conso_100km,
      created_at: rent.created_at,
      updated_at: rent.updated_at,
    };
  };


  const fetchDirections = async () => {
    try {
      setOpenLoadingSimpleBackdrop(true);
      const result = await DirectionService.getDir();
      const payload = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
        ? result
        : [];
      const formatted = payload.map((row) => ({
        label: row.name,
        value: row.direction_id,
      }));
      setDirectionOptions(formatted);
    } catch (error) {
      setMessage({
        open: true,
        color: "red",
        messageInfo:
          "Erreur lors de la recuperation des directions. Veuillez reessayer.",
      });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };


  const fetchRents = async (directionId) => {
    if (!directionId) {
      setRows([]);
      return;
    }
    try {
      setOpenLoadingSimpleBackdrop(true);
      const result = await MissionRentService.getRentSettings(directionId);
      const payload = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
        ? result
        : [];
      setRows(payload.map((rent, index) => mapRentToRow(rent, index)));
    } catch (error) {
      setMessage({
        open: true,
        color: "red",
        messageInfo:
          "Erreur lors de la recuperation des locations. Veuillez reessayer.",
      });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };


  useEffect(() => {
    fetchDirections();
    fetchRents(directionUser);
  }, []);


  const openCreateDialog = () => {
    setFormMode("create");
    setFormValues((prev) => ({
      ...initialFormValues,
      direction_id: selectedDirection || null,
    }));
    setFormDialogOpen(true);
  };


  const openEditDialog = (row) => {
    setFormMode("edit");
    setFormValues({
      rent_id: row.rent_id,
      direction_id: row.direction_id,
      rent_type: row.rent_type,
      rent_designation: row.rent_designation,
      rent_paiement: row.rent_paiement,
      rent_amount: row.rent_amount,
      rent_upfuel: row.rent_upfuel,
      rent_conso_100km: row.rent_conso_100km || "",
    });
    setFormDialogOpen(true);
  };


  const closeFormDialog = () => {
    setFormDialogOpen(false);
    setFormValues(initialFormValues);
  };


  const handleSubmitForm = async () => {
    const validationResult = validateRentForm(formValues, {
      requireDirection: true,
    });
    if (!validationResult.valid) {
      setMessage({
        open: true,
        color: "red",
        messageInfo: validationResult.message,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        mission_rent_id: formValues.rent_id,
        direction_id: formValues.direction_id,
        rent_type:
          formValues.rent_type === "other"
            ? formValues.rent_designation
            : formValues.rent_type,
        rent_paiement: formValues.rent_paiement,
        rent_amount: formValues.rent_amount,
        rent_upfuel: formValues.rent_upfuel,
        rent_conso_100km: formValues.rent_conso_100km,
      };


      let result;
        result = await MissionRentService.createRentSetting(payload);


      if (result?.status || result?.status) {
        setMessage({
          open: true,
          color: "green",
          messageInfo: "Location enregistree avec succes.",
        });
        closeFormDialog();
        const directionToRefresh = payload.direction_id;
        if (selectedDirection !== directionToRefresh) {
          setSelectedDirection(directionToRefresh);
        }
        await fetchRents(directionToRefresh);
      } else {
        setMessage({
          open: true,
          color: "red",
          messageInfo:
            result?.message ||
            "Une erreur est survenue lors de l'enregistrement de la location.",
        });
      }
    } catch (error) {
      setMessage({
        open: true,
        color: "red",
        messageInfo:
          "Une erreur est survenue lors de l'enregistrement de la location.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleDeleteRent = async () => {
    if (!deleteDialog.rent_id) {
      return;
    }
    try {
      setOpenLoadingSimpleBackdrop(true);
      const result = await MissionRentService.deleteRentSetting(deleteDialog.rent_id);
      if (result?.status || result?.success) {
        setMessage({
          open: true,
          color: "green",
          messageInfo: "Location supprimee avec succes.",
        });
        await fetchRents(selectedDirection || null);
      } else {
        setMessage({
          open: true,
          color: "red",
          messageInfo:
            result?.message ||
            "Une erreur est survenue lors de la suppression de la location.",
        });
      }
    } catch (error) {
      setMessage({
        open: true,
        color: "red",
        messageInfo:
          "Une erreur est survenue lors de la suppression de la location.",
      });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
      setDeleteDialog({ open: false, rent_id: null });
    }
  };


  const { open, color, messageInfo } = message;


  return (
    <MainContainer {...headerDataImmo}>
              <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />


      <Message
        open={open}
        color={color}
        message={messageInfo}
        onClose={handleCloseMessage}
      />
           <Grid container item xs={12}>
      <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
              >
                CREER
              </Button>
                      <Spacer x={2} />
                <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={() =>
                  selectedDirection
                    ? fetchRents(selectedDirection)
                    : fetchDirections()
                }
              >
                Filtrer
              </Button>
              </Grid>
      <Spacer height={20} />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={directionOptions}
              value={directionOptions.find((dir) => dir.value === selectedDirection) || null}
              onChange={(_, newValue) => setSelectedDirection(newValue ? newValue.value : null)}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Direction"
                  size="small"
                />
              )}
            />
          </Grid>
         
        </Grid>
       <Paper className={classes.paper} variant="outlined">
               <Title>Listes des locations</Title>
       
               <div style={{ height: "58vh", width: "100%", paddingTop: "1%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            localeText={{ noRowsLabel: "Aucune location trouvee." }}
          />
        </div>
      </Paper>


      <Dialog open={formDialogOpen} onClose={closeFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle id="form-dialog-title">
          {formMode === "edit" ? "Modifier la location" : "Nouvelle location"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={directionOptions}
                value={getDirectionOption(formValues.direction_id)}
                onChange={(_, newValue) =>
                  setFormValues((prev) => ({
                    ...prev,
                    direction_id: newValue ? newValue.value : null,
                  }))
                }
                getOptionLabel={(option) => option?.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Direction"
                    required
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={RENT_TYPES}
                value={
                  RENT_TYPES.find((type) => type.value === formValues.rent_type) ||
                  null
                }
                onChange={(_, newValue) =>
                  setFormValues((prev) => ({
                    ...prev,
                    rent_type: newValue ? newValue.value : "",
                    rent_designation:
                      newValue && newValue.value !== "other"
                        ? ""
                        : prev.rent_designation,
                  }))
                }
                getOptionLabel={(option) => option?.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type de location"
                    required
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={PAYMENT_TYPES}
                value={
                  PAYMENT_TYPES.find(
                    (payment) => payment.value === formValues.rent_paiement
                  ) || null
                }
                onChange={(_, newValue) =>
                  setFormValues((prev) => ({
                    ...prev,
                    rent_paiement: newValue ? newValue.value : "",
                  }))
                }
                getOptionLabel={(option) => option?.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Mode de paiement"
                    required
                    size="small"
                  />
                )}
              />
            </Grid>
            {formValues.rent_type === "other" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Designation"
                  value={formValues.rent_designation}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      rent_designation: event.target.value,
                    }))
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Montant location (Ar)"
                type="number"
                value={formValues.rent_amount}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    rent_amount: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Prix unitaire carburant (Ar/L)"
                type="number"
                value={formValues.rent_upfuel}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    rent_upfuel: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Consommation / 100km"
                type="number"
                value={formValues.rent_conso_100km}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    rent_conso_100km: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormDialog} color="error">Annuler</Button>
          <LoadingButton
            onClick={handleSubmitForm}
            loading={isSubmitting}
            variant="contained"
            color="info"
          >
            Enregistrer
          </LoadingButton>
        </DialogActions>
      </Dialog>


      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, rent_id: null })}
      >
        <DialogTitle id="form-dialog-title">Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Etes-vous sur de vouloir supprimer cette location ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, rent_id: null })}>
            Annuler
          </Button>
          <Button
            onClick={handleDeleteRent}
            color="error"
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};


export default RentParameter;




